import React, { useState, useRef, useEffect } from 'react';

interface SelectFieldProps {
  label: string;
  options?: string[] | null; // Changed type to allow null for dates
  onOptionChange?: (option: string) => void;
  type?: 'option' | 'date'; // Added type prop
}

export const SelectField: React.FC<SelectFieldProps> = ({ label, options, onOptionChange, type = 'option', ...props }) => {
  const [selectedOption, setSelectedOption] = useState("");
  const [isSelectFocused, setSelectFocused] = useState(false);
  const [isDateFocused, setDateFocused] = useState(false);

  const dateRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isFocused = isSelectFocused || isDateFocused || selectedOption !== "";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setSelectFocused(false);
        setDateFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLabelClick = () => {
    if (type === 'date') {
      dateRef.current?.focus();
      dateRef.current?.click();
    } else {
      selectRef.current?.focus();
    }
  };

  return (
    <div ref={containerRef} className="p-3 rounded-xl w-full bg-white relative" onClick={handleLabelClick}>
      <label
        style={{ top: isFocused ? '5px' : '5px', position: 'absolute', left: '16px' }}
        className="font-medium text-[#9392A2] text-xs"
      >
        {label}
      </label>
      {type === 'option' && ( // Render select for options
        <select
          {...props}
          ref={selectRef}
          className="mt-2 text-sm font-medium text-black w-full outline-none pl-1 pt-1"
          onFocus={() => setSelectFocused(true)}
          onBlur={() => setSelectFocused(false)}
          onChange={(e) => {
            setSelectedOption(e.target.value);
            onOptionChange && onOptionChange(e.target.value);
          }}
        >
          {options && options.map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
      )}
      {type === 'date' && ( // Render input for date
        <input
          {...props}
          ref={dateRef}
          type="date"
          className="mt-2 text-sm font-medium text-black w-full outline-none pl-1 pt-1"
          onFocus={() => setDateFocused(true)}
          onBlur={() => setDateFocused(false)}
          onChange={(e) => {
            setSelectedOption(e.target.value);
            onOptionChange && onOptionChange(e.target.value);
          }}
        />
      )}
    </div>
  );
};

export default SelectField;
